import { produce } from 'immer';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useBreakpoints from '../../../../../common/hooks/useBreakpoints';
import Breadcrumbs from '../../../../../common/widgets/Breadcrumbs';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import { config } from '../../../config';
import { HelpMediaType } from '../../../enums';
import { rewriteImageUrl } from '../../../helpers';
import { HelpMediaModel, HelpSectionDetailsModel } from '../../../models/Help';
import { resources } from '../../../resources/resources';
import { helpService } from '../../../services/help';
import applicationCrumbs from '../../ApplicationCrumbs';
import { HelpMenu } from '../HelpMenu';

interface HelpSectionDetailsPageState {
  details?: HelpSectionDetailsModel;
  isSectionLoading: boolean;
}

export const HelpSectionDetailsPage = () => {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const [state, setState] = useState<HelpSectionDetailsPageState>({
    isSectionLoading: true,
  });
  const { sectionId } = useParams();
  const { isTablet } = useBreakpoints();

  const loadSectionDetails = useCallback(
    async (abortController?: AbortController) => {
      if (!sectionId) return;
      setState(
        produce(d => {
          d.isSectionLoading = true;
        }),
      );
      const resp = await helpService.getHelpSectionDetails(
        Number.parseInt(sectionId),
        {
          signal: abortController?.signal,
        },
      );
      if (!resp.hasErrors) {
        setState({ details: resp.data, isSectionLoading: false });
      }
    },
    [sectionId],
  );

  useEffect(() => {
    const abortController = new AbortController();
    loadSectionDetails(abortController);
    return () => {
      abortController.abort();
    };
  }, [sectionId, loadSectionDetails]);

  return (
    <>
      <Breadcrumbs entries={[crumbs.home, crumbs.help]} />
      <section className="section-main section-actions help-section-page">
        <div className="container">
          <div className="area-actions">
            <div className="action-panel">
              <div className="action-panel-content secondary-nav help-nav">
                <HelpMenu />
              </div>
            </div>
          </div>
          <div className="area-content">
            <div className="panel questions-panel padding-xl mb-30">
              <Loader loading={state.isSectionLoading} loader={<Skeleton />}>
                {!!state.details && (
                  <>
                    <h1 className="title-back-btn">
                      {isTablet && (
                        <Link to="/help" className="btn btn-black-mobile">
                          <span className="btn-icon">
                            <Icons.ArrowBackMobile />
                          </span>
                        </Link>
                      )}
                      {state.details.name}
                    </h1>
                    <ul className="questions-list list-reset">
                      {state.details.questionsAndAnswers.map((e, index) => (
                        <li key={index} id={e.questionId.toString()}>
                          <div>
                            <h2
                              dangerouslySetInnerHTML={{
                                __html: e.question,
                              }}
                            />
                            <div>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: e.answer,
                                }}
                              />
                              {e.media.map(e => {
                                if (e.mediaType === HelpMediaType.Image)
                                  return <HelpImage key={e.path} {...e} />;
                                if (e.mediaType === HelpMediaType.Video)
                                  return <HelpVideo key={e.path} {...e} />;
                                if (e.mediaType === HelpMediaType.File)
                                  return <HelpFile key={e.path} {...e} />;
                              })}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Loader>
            </div>
            <div className="panel help-feedback-panel padding-xl mb-60">
              <Loader loading={state.isSectionLoading} loader={<Skeleton />}>
                {!!state.details && (
                  <RateSectionComponent sectionId={state.details.sectionId} />
                )}
              </Loader>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const HelpImage = (props: HelpMediaModel) => {
  return (
    <div className="help-answer-img">
      <img src={rewriteImageUrl(props.path)} alt="" />
      <span>{props.description}</span>
    </div>
  );
};

const HelpVideo = (props: HelpMediaModel) => {
  return (
    <div className="help-answer-video">
      <div>
        <video controls>
          <source src={`${config.videosUrl}/${props.path}`} />
        </video>
      </div>
      <span>{props.description}</span>
    </div>
  );
};

const HelpFile = (props: HelpMediaModel) => {
  return (
    <div>
      <a
        href={`${config.filesUrl}/${props.path}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {props.description}
      </a>
    </div>
  );
};

interface RateSectionComponentProps {
  sectionId: number;
}

interface RateSectionComponentState {
  showFeedbackMessage?: boolean;
}

const RateSectionComponent = (props: RateSectionComponentProps) => {
  const { sectionId } = props;
  const { t: translate } = useTranslation();
  const [state, setState] = useState<RateSectionComponentState>({});
  const navigate = useNavigate();

  const rateSection = async (isPositive: boolean) => {
    helpService.rateHelpSection(sectionId, isPositive);
    if (isPositive) {
      setState({ showFeedbackMessage: true });
    } else {
      navigate('/help/contact');
    }
  };

  if (state.showFeedbackMessage)
    return (
      <h2 className="feedback-thankyou">
        {translate(resources.help.contact.feedback)}
      </h2>
    );

  return (
    <>
      <h2>{translate(resources.help.wasAnswerUseful)}</h2>
      <div className="feedback-buttons">
        <button onClick={() => rateSection(true)} className="btn">
          <span className="btn-text">{translate(resources.yes)}</span>
          <span className="btn-icon">
            <Icons.Like />
          </span>
        </button>
        <button onClick={() => rateSection(false)} className="btn">
          <span className="btn-text">{translate(resources.no)}</span>
          <span className="btn-icon">
            <Icons.Dislike />
          </span>
        </button>
      </div>
    </>
  );
};
